import { RadialProgress, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { Image01Icon } from '@vizit/icons';
import { Asset, LibraryEmptyState } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import type * as React from 'react';
import { AssetGrid } from '../asset-grid';

/** Where an image is in processing: still processing, done, or given up on after the processing window. */
export type BenchmarkImageStatus = 'processing' | 'ready' | 'failed';

export interface BenchmarkImageItem {
  /**
   * Unique identifier for the image
   */
  id: string;
  /**
   * The file name, shown above the tile
   */
  name: string;
  /**
   * Thumbnail URL; `null` until the image is uploaded
   */
  thumbnailUrl: string | null;
  /**
   * Full-size image to show when the thumbnail fails to load, e.g. a rendition that does not exist yet
   */
  fallbackUrl?: string | null;
  /**
   * Where the image is in processing
   */
  status: BenchmarkImageStatus;
}

export interface BenchmarkImageGridProps {
  /**
   * The images to show, in display order
   */
  images: BenchmarkImageItem[];
  /**
   * Total images in the benchmark
   */
  totalCount: number;
  /**
   * Images needed before the benchmark can be scored against, used in the empty state copy
   */
  minimumImages: number;
  /**
   * Badge copy on tiles still processing: "Processing…" without a category, "Scoring…" once one exists
   *
   * @optional
   * @default 'Processing…'
   */
  processingLabel?: string;
  /**
   * Whether the images are loading
   *
   * @optional
   * @default false
   */
  isLoading?: boolean;
  /**
   * Action shown in the empty state, e.g. the "Upload images" button
   *
   * @optional
   */
  emptyStateAction?: React.ReactNode;
  /**
   * Called when a processed image is pressed
   *
   * @optional
   */
  onImagePress?: (id: string) => void;
  /**
   * Additional CSS classes for the root element
   *
   * @optional
   */
  className?: string;
}

function ProcessingBadge({ label }: { label: string }) {
  return (
    <span className="absolute top-2 left-2 z-10 flex items-center gap-2 border border-secondary bg-white px-3 py-1">
      <RadialProgress isIndeterminate size={8} aria-hidden />
      <Text size={TextSize.Xs} weight={TextWeight.Medium} color={TextColor.Secondary}>
        {label}
      </Text>
    </span>
  );
}

function BenchmarkImageTile({
  image,
  processingLabel,
  onImagePress,
}: {
  image: BenchmarkImageItem;
  processingLabel: string;
  onImagePress?: (id: string) => void;
}) {
  const isProcessing = image.status === 'processing';
  const isPressable = image.status === 'ready' && !!onImagePress;
  const accessibleName =
    image.status === 'processing'
      ? `${image.name}, ${processingLabel.replace('…', '')}`
      : image.status === 'failed'
        ? `${image.name}, could not be processed`
        : image.name;

  return (
    <Asset
      id={image.id}
      isUnscored
      showScoreBar={false}
      isViewOnly={!isPressable}
      onPress={isPressable ? () => onImagePress?.(image.id) : undefined}
      aria-label={accessibleName}
      {...(isPressable ? {} : { role: 'group' })}
    >
      <Asset.Label className="truncate pb-2">{image.name}</Asset.Label>
      <div className="relative">
        <Asset.Image
          name={image.name}
          src={image.thumbnailUrl}
          fallbackSrc={image.fallbackUrl}
          className={cx(isProcessing && 'opacity-[0.45] grayscale-[60%]')}
        />
        {isProcessing && <ProcessingBadge label={processingLabel} />}
      </div>
      {isProcessing && <div className="mt-2 h-4 w-full animate-pulse bg-tertiary" aria-hidden />}
      {image.status === 'failed' && (
        <Text size={TextSize.Xs} color={TextColor.Quaternary} className="pt-2">
          Couldn't process
        </Text>
      )}
    </Asset>
  );
}

/**
 * The images of a benchmark as a grid of tiles, each captioned with its file name. Tiles still processing are
 * dimmed with a spinner badge and a pulsing bar where the score bar goes, and each resolves on its own. Processed
 * tiles open the image preview. Without images it shows how many the benchmark needs and an upload action.
 */
export function BenchmarkImageGrid(props: BenchmarkImageGridProps) {
  const {
    images,
    totalCount,
    minimumImages,
    processingLabel = 'Processing…',
    isLoading = false,
    emptyStateAction,
    onImagePress,
    className,
  } = props;

  if (!isLoading && images.length === 0) {
    return (
      <LibraryEmptyState
        heading="No reference images yet."
        description={`Add at least ${minimumImages} images from your gallery, then pick a product category to score them.`}
        icon={Image01Icon}
        className={className}
      >
        {emptyStateAction}
      </LibraryEmptyState>
    );
  }

  return (
    <AssetGrid totalCount={totalCount} isLoading={isLoading} showCount={false} className={className}>
      {images.map((image) => (
        <BenchmarkImageTile
          key={image.id}
          image={image}
          processingLabel={processingLabel}
          onImagePress={onImagePress}
        />
      ))}
    </AssetGrid>
  );
}
